/**
 * Arquivos da licitação (17 §3.2, aba Arquivos): envio de vários arquivos de uma vez (arrastar ou escolher),
 * tipo sugerido pelo nome, e anexação dos arquivos publicados no PNCP (Edge Function pncp-arquivo, D70).
 */
import { useEffect, useState, type DragEvent } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import ZipIcon from "@mui/icons-material/FolderZipOutlined";
import LerIcon from "@mui/icons-material/PlagiarismOutlined";
import { useGetList, useNotify, usePermissions, useRefresh } from "react-admin";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloudDownloadIcon from "@mui/icons-material/CloudDownloadOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import OpenIcon from "@mui/icons-material/OpenInNewOutlined";
import UploadIcon from "@mui/icons-material/UploadFileOutlined";
import { lerControlePncp } from "@sobral/dominio";
import { useCelular } from "../componentes";
import { formatarDataHora, formatarNumero } from "../lib/formato";
import { consultarArquivos, tipoDeArquivo, type ArquivoPncp } from "../lib/pncp";
import type { Permissoes } from "../lib/provedores";
import { supabase } from "../lib/supabase";
import { DialogoLeitura, type LicitacaoLeitura } from "./licitacao-leitura";
import { sincronizarOneDriveEmSegundoPlano } from "./onedrive";

const BUCKET = "licitacoes";

export const TIPOS_ARQUIVO = [
  { id: "edital", name: "Edital" },
  { id: "termo_referencia", name: "Termo de referência" },
  { id: "anexo", name: "Anexo" },
  { id: "proposta", name: "Proposta" },
  { id: "ata", name: "Ata" },
  { id: "outro", name: "Outro" },
];

const ACEITOS = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/zip",
  "application/x-zip-compressed",
];

interface Arquivo {
  id: string;
  tipo: string;
  arquivo_caminho: string;
  arquivo_nome: string | null;
  origem_url: string | null;
  criado_em: string;
}

async function abrir(caminho: string) {
  const { data } = await supabase.storage.from(BUCKET).createSignedUrl(caminho, 300);
  if (data) window.open(data.signedUrl, "_blank", "noopener");
}

/* ------------------------------------ Conteúdo de ZIP ------------------------------------ */

interface EntradaZip {
  nome: string;
  dados: Uint8Array;
}

const TIPO_POR_EXTENSAO: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  odt: "application/vnd.oasis.opendocument.text",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  zip: "application/zip",
};

const extensao = (nome: string) => nome.split(".").pop()?.toLowerCase() ?? "";
const tipoDoNome = (nome: string) => TIPO_POR_EXTENSAO[extensao(nome)] ?? "application/octet-stream";
const nomeCurto = (caminho: string) => caminho.split("/").pop() ?? caminho;

async function baixar(caminho: string): Promise<Uint8Array> {
  const { data, error } = await supabase.storage.from(BUCKET).download(caminho);
  if (error || !data) throw new Error(error?.message ?? "arquivo indisponível");
  return new Uint8Array(await data.arrayBuffer());
}

/** Lista o ZIP no navegador (sem extrair no servidor), com visualização rápida de PDF e imagem e opção de anexar um arquivo. */
function DialogoZip({ arquivo, licitacao, aoFechar }: { arquivo: Arquivo; licitacao: LicitacaoLeitura; aoFechar: () => void }) {
  const licitacaoId = licitacao.id;
  const notify = useNotify();
  const refresh = useRefresh();
  const celular = useCelular();
  const [entradas, setEntradas] = useState<EntradaZip[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [vendo, setVendo] = useState<{
    nome: string;
    url: string;
    tipo: string;
  } | null>(null);
  const [anexando, setAnexando] = useState<string | null>(null);
  const [lendo, setLendo] = useState<EntradaZip | null>(null);

  useEffect(() => {
    let ativo = true;
    void (async () => {
      try {
        const [{ unzipSync }, bytes] = await Promise.all([import("fflate"), baixar(arquivo.arquivo_caminho)]);
        const conteudo = unzipSync(bytes);
        const lista = Object.entries(conteudo)
          .filter(([nome, dados]) => !nome.endsWith("/") && dados.length > 0 && !nome.startsWith("__MACOSX/"))
          .map(([nome, dados]) => ({ nome, dados }))
          .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR", { numeric: true }));
        if (ativo) setEntradas(lista);
      } catch (e) {
        if (ativo) setErro(`Não foi possível abrir o ZIP: ${(e as Error).message}`);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [arquivo.arquivo_caminho]);

  useEffect(
    () => () => {
      if (vendo) URL.revokeObjectURL(vendo.url);
    },
    [vendo],
  );

  function ver(e: EntradaZip) {
    const tipo = tipoDoNome(e.nome);
    const url = URL.createObjectURL(new Blob([e.dados.slice()], { type: tipo }));
    if (tipo === "application/pdf" || tipo.startsWith("image/")) setVendo({ nome: nomeCurto(e.nome), url, tipo });
    else {
      const link = document.createElement("a");
      link.href = url;
      link.download = nomeCurto(e.nome);
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    }
  }

  async function anexar(e: EntradaZip) {
    const nome = nomeCurto(e.nome);
    const tipo = tipoDoNome(nome);
    if (!ACEITOS.includes(tipo)) {
      notify("Formato não aceito para anexar.", { type: "warning" });
      return;
    }
    setAnexando(e.nome);
    try {
      const caminho = `${licitacaoId}/${new Date().toISOString().replace(/[:.]/g, "-")}-${Math.random().toString(36).slice(2, 7)}.${extensao(nome) || "bin"}`;
      const { error: erroUpload } = await supabase.storage.from(BUCKET).upload(caminho, new Blob([e.dados.slice()], { type: tipo }), {
        contentType: tipo,
        upsert: false,
      });
      if (erroUpload) throw new Error(erroUpload.message);
      const { error } = await supabase.from("licitacao_arquivo").insert({
        licitacao_id: licitacaoId,
        tipo: tipoDeArquivo(nome),
        arquivo_caminho: caminho,
        arquivo_nome: nome,
      });
      if (error) throw new Error(error.message);
      notify(`${nome} anexado à licitação.`, { type: "success" });
      sincronizarOneDriveEmSegundoPlano(licitacaoId);
      refresh();
    } catch (err) {
      notify(`Não foi possível anexar: ${(err as Error).message}`, {
        type: "error",
      });
    } finally {
      setAnexando(null);
    }
  }

  return (
    <Dialog open onClose={aoFechar} fullWidth maxWidth={vendo ? "lg" : "md"} fullScreen={celular || Boolean(vendo)}>
      <DialogTitle>{vendo ? vendo.nome : `Conteúdo de ${arquivo.arquivo_nome ?? "ZIP"}`}</DialogTitle>
      <DialogContent>
        {erro && <Alert severity="warning">{erro}</Alert>}
        {!entradas && !erro && <CircularProgress size={24} />}
        {vendo ? (
          vendo.tipo === "application/pdf" ? (
            <Box component="iframe" src={vendo.url} title={vendo.nome} sx={{ width: "100%", height: "calc(100vh - 160px)", border: 0 }} />
          ) : (
            <Box component="img" src={vendo.url} alt={vendo.nome} sx={{ maxWidth: "100%" }} />
          )
        ) : (
          entradas && (
            <Table size="small">
              <TableBody>
                {entradas.map((e) => {
                  const visualiza = /^(pdf|png|jpe?g)$/.test(extensao(e.nome));
                  return (
                    <TableRow key={e.nome} hover>
                      <TableCell sx={{ wordBreak: "break-word" }}>
                        <Button size="small" onClick={() => ver(e)} sx={{ textTransform: "none", textAlign: "left" }}>
                          {e.nome}
                        </Button>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatarNumero(Math.max(1, Math.round(e.dados.length / 1024)))} KB · {visualiza ? "ver" : "baixar"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {extensao(e.nome) === "pdf" && (
                          <Button size="small" startIcon={<LerIcon />} onClick={() => setLendo(e)}>
                            Ler
                          </Button>
                        )}
                        {ACEITOS.includes(tipoDoNome(e.nome)) && (
                          <Button size="small" disabled={anexando !== null} onClick={() => void anexar(e)}>
                            {anexando === e.nome ? "Anexando…" : "Anexar à licitação"}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )
        )}
      </DialogContent>
      {lendo && (
        <DialogoLeitura
          nome={nomeCurto(lendo.nome)}
          pdf={() => Promise.resolve(new Blob([lendo.dados.slice()], { type: "application/pdf" }))}
          licitacao={licitacao}
          aoFechar={() => setLendo(null)}
        />
      )}
      <DialogActions>
        {vendo && <Button onClick={() => setVendo(null)}>Voltar à lista</Button>}
        <Button onClick={aoFechar}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Arquivos publicados no PNCP, com botão para anexar à licitação. */
function ArquivosPncp({ licitacaoId, numeroControle, anexados }: { licitacaoId: string; numeroControle: string; anexados: Set<string> }) {
  const notify = useNotify();
  const refresh = useRefresh();
  const [arquivos, setArquivos] = useState<ArquivoPncp[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [anexando, setAnexando] = useState<string | null>(null);

  useEffect(() => {
    const controle = lerControlePncp(numeroControle);
    if (!controle) return;
    let ativo = true;
    consultarArquivos(controle)
      .then((a) => ativo && setArquivos(a))
      .catch((e: Error) => ativo && setErro(e.message));
    return () => {
      ativo = false;
    };
  }, [numeroControle]);

  async function anexar(lista: ArquivoPncp[]) {
    let feitos = 0;
    const falhas: string[] = [];
    for (const a of lista) {
      setAnexando(a.url);
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        erro?: string;
      }>("pncp-arquivo", {
        body: {
          licitacao_id: licitacaoId,
          url: a.url,
          titulo: a.titulo,
          tipo: tipoDeArquivo(`${a.tipoDocumentoNome} ${a.titulo}`),
        },
      });
      if (error || !data?.ok) {
        let mensagem = error?.message ?? data?.erro ?? "falha";
        const contexto = (error as { context?: Response } | null)?.context;
        if (contexto)
          mensagem =
            (
              (await contexto.json().catch(() => null)) as {
                erro?: string;
              } | null
            )?.erro ?? mensagem;
        falhas.push(`${a.titulo}: ${mensagem}`);
      } else feitos += 1;
    }
    setAnexando(null);
    notify(falhas.length ? `${feitos} arquivo(s) anexado(s). Falharam: ${falhas.join("; ")}` : `${feitos} arquivo(s) anexado(s).`, {
      type: falhas.length ? "warning" : "success",
    });
    if (feitos) {
      sincronizarOneDriveEmSegundoPlano(licitacaoId);
      refresh();
    }
  }

  if (erro) return <Alert severity="warning">Não foi possível conferir os arquivos do PNCP: {erro}</Alert>;
  if (!arquivos) return null;
  const pendentes = arquivos.filter((a) => !anexados.has(a.url));
  // Na captação os arquivos já vêm (D99): a lista do PNCP só aparece quando há algum que não veio.
  if (pendentes.length === 0) return null;
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">Publicados no PNCP e ainda não baixados</Typography>
            {pendentes.length > 1 && (
              <Button size="small" startIcon={<CloudDownloadIcon />} disabled={anexando !== null} onClick={() => void anexar(pendentes)}>
                Anexar todos ({pendentes.length})
              </Button>
            )}
          </Stack>
          <Table size="small">
            <TableBody>
              {arquivos.map((a) => (
                <TableRow key={a.url}>
                  <TableCell>{a.tipoDocumentoNome}</TableCell>
                  <TableCell sx={{ wordBreak: "break-word" }}>{a.titulo}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    {anexados.has(a.url) ? (
                      <Typography variant="caption" color="success.main">
                        Anexado
                      </Typography>
                    ) : (
                      <Button size="small" startIcon={<CloudDownloadIcon />} disabled={anexando !== null} onClick={() => void anexar([a])}>
                        {anexando === a.url ? "Anexando…" : "Anexar"}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>
      </CardContent>
    </Card>
  );
}

export function ArquivosLicitacao({ licitacao, numeroControlePncp }: { licitacao: LicitacaoLeitura; numeroControlePncp: string | null }) {
  const licitacaoId = licitacao.id;
  const notify = useNotify();
  const refresh = useRefresh();
  const { permissions } = usePermissions<Permissoes | null>();
  const admin = permissions?.papel === "admin";
  const [enviando, setEnviando] = useState(0);
  const [sobre, setSobre] = useState(false);
  const [zip, setZip] = useState<Arquivo | null>(null);
  const [lendo, setLendo] = useState<Arquivo | null>(null);
  const { data: arquivos = [] } = useGetList<Arquivo>("licitacao_arquivo", {
    filter: { licitacao_id: licitacaoId },
    pagination: { page: 1, perPage: 500 },
    sort: { field: "criado_em", order: "ASC" },
  });
  const anexados = new Set(arquivos.map((a) => a.origem_url).filter((u): u is string => Boolean(u)));

  async function enviar(lista: FileList | File[] | null) {
    if (!lista) return;
    const validos = Array.from(lista).filter((a) => ACEITOS.includes(a.type));
    const recusados = Array.from(lista).length - validos.length;
    if (recusados)
      notify(`${recusados} arquivo(s) em formato não aceito foram ignorados.`, {
        type: "warning",
      });
    let feitos = 0;
    const falhas: string[] = [];
    setEnviando(validos.length);
    for (const arquivo of validos) {
      try {
        const extensao = arquivo.name.split(".").pop()?.toLowerCase() ?? "pdf";
        const caminho = `${licitacaoId}/${new Date().toISOString().replace(/[:.]/g, "-")}-${Math.random().toString(36).slice(2, 7)}.${extensao}`;
        const { error: erroUpload } = await supabase.storage.from(BUCKET).upload(caminho, arquivo, {
          contentType: arquivo.type,
          upsert: false,
        });
        if (erroUpload) throw new Error(erroUpload.message);
        const { error } = await supabase.from("licitacao_arquivo").insert({
          licitacao_id: licitacaoId,
          tipo: tipoDeArquivo(arquivo.name),
          arquivo_caminho: caminho,
          arquivo_nome: arquivo.name,
        });
        if (error) throw new Error(error.message);
        feitos += 1;
      } catch (e) {
        falhas.push(`${arquivo.name}: ${(e as Error).message}`);
      }
      setEnviando((n) => n - 1);
    }
    notify(falhas.length ? `${feitos} arquivo(s) enviado(s). Falharam: ${falhas.join("; ")}` : `${feitos} arquivo(s) enviado(s).`, {
      type: falhas.length ? "warning" : "success",
    });
    if (feitos) {
      sincronizarOneDriveEmSegundoPlano(licitacaoId);
      refresh();
    }
  }

  async function excluir(a: Arquivo) {
    const { error } = await supabase.from("licitacao_arquivo").delete().eq("id", a.id);
    if (error) {
      notify(`Não foi possível excluir: ${error.message}`, { type: "error" });
      return;
    }
    await supabase.storage.from(BUCKET).remove([a.arquivo_caminho]);
    notify("Arquivo excluído.", { type: "success" });
    refresh();
  }

  const soltar = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setSobre(false);
    void enviar(e.dataTransfer.files);
  };

  return (
    <Stack spacing={2}>
      <Box
        onDragOver={(e) => {
          e.preventDefault();
          setSobre(true);
        }}
        onDragLeave={() => setSobre(false)}
        onDrop={soltar}
        sx={{
          border: "2px dashed",
          borderColor: sobre ? "secondary.main" : "divider",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          bgcolor: sobre ? "action.hover" : "transparent",
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          Arraste os arquivos aqui (edital, termo de referência, anexos) ou
        </Typography>
        <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={enviando > 0}>
          {enviando > 0 ? `Enviando… (${enviando})` : "Escolher arquivos"}
          <input type="file" hidden multiple accept={ACEITOS.join(",")} onChange={(e) => void enviar(e.target.files)} />
        </Button>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          PDF, Word, Excel, imagens ou ZIP, até 50 MB cada. Os arquivos publicados no PNCP já vêm na captação.
        </Typography>
      </Box>

      {arquivos.length > 0 && (
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableBody>
              {arquivos.map((a) => (
                <TableRow key={a.id}>
                  <TableCell sx={{ wordBreak: "break-word" }}>
                    <Button
                      size="small"
                      startIcon={<OpenIcon />}
                      onClick={() => void abrir(a.arquivo_caminho)}
                      sx={{ textTransform: "none", textAlign: "left" }}
                    >
                      {a.arquivo_nome ?? "Abrir"}
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ pl: 1 }}>
                      {a.origem_url ? "Do PNCP · " : ""}
                      {formatarDataHora(a.criado_em)}
                    </Typography>
                    {/\.pdf$/i.test(a.arquivo_caminho) && (
                      <Button size="small" startIcon={<LerIcon />} onClick={() => setLendo(a)} sx={{ ml: 0.5 }}>
                        Ler prazos e itens
                      </Button>
                    )}
                    {/\.zip$/i.test(a.arquivo_caminho) && (
                      <Button size="small" startIcon={<ZipIcon />} onClick={() => setZip(a)} sx={{ ml: 0.5 }}>
                        Ver conteúdo
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ width: 56 }}>
                    {admin && (
                      <Tooltip title="Excluir arquivo">
                        <IconButton size="small" onClick={() => void excluir(a)} aria-label="Excluir arquivo">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {zip && <DialogoZip arquivo={zip} licitacao={licitacao} aoFechar={() => setZip(null)} />}
      {lendo && (
        <DialogoLeitura
          nome={lendo.arquivo_nome ?? "documento.pdf"}
          pdf={async () =>
            new Blob([(await baixar(lendo.arquivo_caminho)).slice()], {
              type: "application/pdf",
            })
          }
          licitacao={licitacao}
          aoFechar={() => setLendo(null)}
        />
      )}
      {numeroControlePncp && <ArquivosPncp licitacaoId={licitacaoId} numeroControle={numeroControlePncp} anexados={anexados} />}
    </Stack>
  );
}
